import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck, Phone, MessageCircle, MapPin, HelpCircle, WifiOff, Wifi, RefreshCw, CloudUpload, Camera } from "lucide-react";
import api, { formatApiError, DEFAULT_AVATAR } from "@/lib/api";
import { enqueueVolunteer } from "@/lib/offlineQueue";
import { useOffline } from "@/hooks/useOffline";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MemberIdCard } from "@/components/MemberIdCard";

const PVC = [
  { v: "has_pvc", l: "I have my PVC" },
  { v: "needs_transfer", l: "Needs transfer to Nasarawa" },
  { v: "registering_new", l: "Registering new" },
];

export default function Volunteer() {
  const [lgas, setLgas] = useState([]);
  const [banks, setBanks] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [centers, setCenters] = useState([]);

  useEffect(() => {
    api.get("/structure/lgas").then((r) => setLgas(r.data.lgas)).catch(() => {});
    api.get("/content/banks").then((r) => setBanks(r.data)).catch(() => {});
    api.get("/content/faqs").then((r) => setFaqs(r.data)).catch(() => {});
    api.get("/content/inec-centers").then((r) => setCenters(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <section className="topo-bg text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <span className="eyebrow text-secondary">Volunteer Portal</span>
          <h1 className="font-heading font-black text-4xl md:text-5xl mt-3">Join the Movement</h1>
          <p className="mt-4 text-lg text-emerald-50 max-w-2xl">
            Register as an official member, get your digital ID card, and connect to your ward coordinator.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-16">
        <OfflineBanner />
        <Tabs defaultValue="member">
          <TabsList className="grid w-full grid-cols-3 rounded-full h-auto p-1 bg-muted mb-8">
            <TabsTrigger value="member" data-testid="tab-member" className="rounded-full py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm">Become a Member</TabsTrigger>
            <TabsTrigger value="volunteer" data-testid="tab-volunteer" className="rounded-full py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm">Quick Volunteer</TabsTrigger>
            <TabsTrigger value="pvc" data-testid="tab-pvc" className="rounded-full py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm">PVC Helpdesk</TabsTrigger>
          </TabsList>

          <TabsContent value="member"><MemberFlow lgas={lgas} banks={banks} /></TabsContent>
          <TabsContent value="volunteer"><QuickVolunteer lgas={lgas} /></TabsContent>
          <TabsContent value="pvc"><PvcDesk faqs={faqs} centers={centers} /></TabsContent>
        </Tabs>
      </section>
    </div>
  );
}

/* ---------- Multi-step member registration ---------- */
function MemberFlow({ lgas, banks }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [otpToken, setOtpToken] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [bankVerified, setBankVerified] = useState(false);
  const [member, setMember] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const [customPosition, setCustomPosition] = useState("");
  const [f, setF] = useState({
    full_name: "", phone: "", gender: "", age: "", lga: "", ward: "", polling_unit: "",
    pvc_status: "", bank_name: "", account_number: "", account_name: "",
    photo: "", role_type: "member", position: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const lga = lgas.find((l) => l.name === f.lga);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    setPhotoPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/members/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo", data.image_path);
      toast.success("Photo added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setPhotoPreview(""); }
    finally { setPhotoUploading(false); }
  };

  const step1Valid = f.full_name && f.phone.length >= 11 && f.gender && f.age && (f.role_type === "member" || (f.role_type === "official" && f.position && (f.position !== "Other" || customPosition.trim()))) && !photoUploading;
  const step2Valid = f.lga && f.ward.trim() && f.polling_unit.trim() && f.pvc_status;
  const step4Valid = f.bank_name && f.account_number.length === 10 && f.account_name && bankVerified;

  const sendOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/otp/send", { phone: f.phone });
      setOtpToken(data.otp_token); setOtpSent(true);
      toast.success(`OTP sent (demo). Your code is ${data.dev_code}`, { duration: 8000 });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/otp/verify", { phone: f.phone, otp: otpInput });
      setOtpToken(data.otp_token);
      toast.success("Phone verified!");
      setStep(4);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const verifyBank = async () => {
    setLoading(true);
    try {
      await api.post("/bank/resolve", { account_number: f.account_number, bank_name: f.bank_name });
      setBankVerified(true);
      toast.success("Bank account verified (demo).");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const finalPosition = f.role_type === "official"
        ? (f.position === "Other" ? customPosition.trim() : f.position)
        : null;
      const { data } = await api.post("/members/register", {
        ...f, position: finalPosition, age: parseInt(f.age, 10), otp_token: otpToken,
      });
      setMember(data.member);
      setStep(5);
      toast.success("Welcome to the London Boy Movement!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (step === 5 && member) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4"><CheckCircle2 className="w-9 h-9 text-primary" /></div>
        <h2 className="font-heading font-extrabold text-2xl">You're Officially In!</h2>
        <p className="text-muted-foreground mt-2 mb-8">Member ID <span className="font-mono font-bold text-primary">{member.member_id}</span>. Download and share your card.</p>
        <MemberIdCard member={member} />
      </motion.div>
    );
  }

  const steps = ["Personal", "Voting", "Verify", "Banking"];
  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8">
      {/* stepper */}
      <div className="flex items-center justify-between mb-8">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${step >= i + 1 ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>{i + 1}</div>
            {i < steps.length - 1 && <div className={`h-1 flex-1 mx-1 rounded ${step > i + 1 ? "bg-primary" : "bg-muted"}`} />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <h3 className="font-heading font-bold text-xl mb-2">Personal Information</h3>
          <div className="flex items-center gap-4">
            <img src={photoPreview || DEFAULT_AVATAR} alt="member" className="w-20 h-20 rounded-2xl object-cover ring-2 ring-primary/15 bg-muted" />
            <div>
              <label className="inline-flex items-center gap-2 rounded-full border-2 border-primary text-primary font-bold px-4 py-2 text-sm cursor-pointer hover:bg-primary hover:text-white transition-colors">
                {photoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                {photoUploading ? "Uploading..." : (f.photo ? "Change Photo" : "Snap / Upload Photo")}
                <input data-testid="reg-photo" type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
              </label>
              <p className="text-xs text-muted-foreground mt-1.5">For your membership ID card (optional).</p>
            </div>
          </div>
          <Field label="Full Name"><Input data-testid="reg-full-name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="e.g. Musa Abdullahi" /></Field>
          <Field label="Phone Number"><Input data-testid="reg-phone" value={f.phone} onChange={(e) => set("phone", e.target.value.replace(/\D/g, ""))} placeholder="08030000000" maxLength={11} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Gender">
              <Select value={f.gender} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger data-testid="reg-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Age"><Input data-testid="reg-age" type="number" value={f.age} onChange={(e) => set("age", e.target.value)} placeholder="e.g. 28" /></Field>
          </div>
          <Field label="Your role in the movement">
            <Select value={f.role_type} onValueChange={(v) => { set("role_type", v); if (v === "member") set("position", ""); }}>
              <SelectTrigger data-testid="reg-role-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Ordinary Member</SelectItem>
                <SelectItem value="official">I hold a position / office</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {f.role_type === "official" && (
            <Field label="Position / Office">
              <Select value={f.position} onValueChange={(v) => { set("position", v); if (v !== "Other") setCustomPosition(""); }}>
                <SelectTrigger data-testid="reg-position"><SelectValue placeholder="Select your position" /></SelectTrigger>
                <SelectContent>
                  {["State Executive", "LGA Coordinator", "Ward Coordinator", "Polling Unit Agent", "Youth Leader", "Women Leader", "Mobiliser", "Volunteer Coordinator", "Other"].map((p) => <SelectItem key={p} value={p}>{p === "Other" ? "Other (specify)" : p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          {f.role_type === "official" && f.position === "Other" && (
            <Field label="Specify your position">
              <Input data-testid="reg-position-other" value={customPosition} onChange={(e) => setCustomPosition(e.target.value)} placeholder="e.g. Local Government Publicity Secretary" />
            </Field>
          )}
          <Nav onNext={() => setStep(2)} nextDisabled={!step1Valid} />
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="font-heading font-bold text-xl mb-2">Voting Data</h3>
          <Field label="LGA">
            <Select value={f.lga} onValueChange={(v) => { set("lga", v); set("ward", ""); set("polling_unit", ""); }}>
              <SelectTrigger data-testid="reg-lga"><SelectValue placeholder="Select LGA" /></SelectTrigger>
              <SelectContent>{lgas.map((l) => <SelectItem key={l.code} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Ward">
            <Input data-testid="reg-ward" list="ward-suggestions" value={f.ward} onChange={(e) => set("ward", e.target.value)} disabled={!lga} placeholder="Type or pick your ward" />
            <datalist id="ward-suggestions">{lga?.wards.map((w) => <option key={w} value={w} />)}</datalist>
          </Field>
          <Field label="Polling Unit">
            <Input data-testid="reg-pu" value={f.polling_unit} onChange={(e) => set("polling_unit", e.target.value)} disabled={!f.ward.trim()} placeholder="Enter your polling unit exactly as on your PVC" />
          </Field>
          <Field label="PVC Status">
            <Select value={f.pvc_status} onValueChange={(v) => set("pvc_status", v)}>
              <SelectTrigger data-testid="reg-pvc"><SelectValue placeholder="Select PVC status" /></SelectTrigger>
              <SelectContent>{PVC.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Nav onBack={() => setStep(1)} onNext={() => setStep(3)} nextDisabled={!step2Valid} />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <h3 className="font-heading font-bold text-xl mb-2 flex items-center gap-2"><Phone className="w-5 h-5 text-primary" /> Verify Your Phone</h3>
          <p className="text-sm text-muted-foreground">We'll send a one-time code to <b>{f.phone}</b>. (Demo mode: the code is shown in a pop-up.)</p>
          {!otpSent ? (
            <Button data-testid="reg-send-otp" onClick={sendOtp} disabled={loading} className="rounded-full bg-primary font-bold gap-2">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Send OTP Code</Button>
          ) : (
            <>
              <Field label="Enter OTP Code"><Input data-testid="reg-otp-input" value={otpInput} onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" maxLength={6} /></Field>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={sendOtp} disabled={loading} className="rounded-full text-sm">Resend</Button>
                <Button data-testid="reg-verify-otp" onClick={verifyOtp} disabled={loading || otpInput.length !== 6} className="rounded-full bg-primary font-bold gap-2 flex-1">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Verify Phone</Button>
              </div>
            </>
          )}
          <Nav onBack={() => setStep(2)} hideNext />
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <h3 className="font-heading font-bold text-xl mb-1 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> Banking Details</h3>
          <p className="text-xs text-muted-foreground bg-secondary/10 border border-secondary/30 rounded-lg px-3 py-2">₦2,000 onboarding support is disbursed after admin verification. One bank account = one member.</p>
          <Field label="Bank Name">
            <Select value={f.bank_name} onValueChange={(v) => { set("bank_name", v); setBankVerified(false); }}>
              <SelectTrigger data-testid="reg-bank"><SelectValue placeholder="Select Bank" /></SelectTrigger>
              <SelectContent>{banks.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Account Number"><Input data-testid="reg-account-number" value={f.account_number} onChange={(e) => { set("account_number", e.target.value.replace(/\D/g, "")); setBankVerified(false); }} placeholder="10-digit NUBAN" maxLength={10} /></Field>
          <Field label="Account Name"><Input data-testid="reg-account-name" value={f.account_name} onChange={(e) => set("account_name", e.target.value)} placeholder="Name on the account" /></Field>
          {!bankVerified ? (
            <Button data-testid="reg-verify-bank" onClick={verifyBank} disabled={loading || !f.bank_name || f.account_number.length !== 10} variant="outline" className="rounded-full border-2 border-primary text-primary font-bold gap-2">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Verify Account</Button>
          ) : (
            <div className="flex items-center gap-2 text-primary text-sm font-semibold"><CheckCircle2 className="w-5 h-5" /> Account verified</div>
          )}
          <Nav onBack={() => setStep(3)} onNext={submit} nextLabel="Complete Registration" nextDisabled={!step4Valid || loading} loading={loading} />
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-sm font-semibold text-slate-700 mb-1.5 block">{label}</Label>{children}</div>;
}

function Nav({ onBack, onNext, nextDisabled, nextLabel = "Continue", hideNext, loading }) {
  return (
    <div className="flex items-center justify-between pt-4">
      {onBack ? <Button variant="ghost" onClick={onBack} className="rounded-full gap-1"><ArrowLeft className="w-4 h-4" /> Back</Button> : <span />}
      {!hideNext && <Button data-testid="reg-next" onClick={onNext} disabled={nextDisabled} className="rounded-full bg-primary font-bold gap-2">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{nextLabel} <ArrowRight className="w-4 h-4" /></Button>}
    </div>
  );
}

/* ---------- Offline status banner ---------- */
function OfflineBanner() {
  const { online, pending, syncing, doSync } = useOffline();
  if (online && pending === 0) return null;
  return (
    <div data-testid="offline-banner" className={`mb-6 rounded-2xl border px-4 py-3 flex items-center justify-between gap-3 ${online ? "bg-primary/5 border-primary/20" : "bg-amber-50 border-amber-200"}`}>
      <div className="flex items-center gap-2 text-sm">
        {online ? <Wifi className="w-5 h-5 text-primary" /> : <WifiOff className="w-5 h-5 text-amber-600" />}
        <span className="font-semibold">
          {online ? "Back online" : "You are offline"}
          {pending > 0 && <span className="text-muted-foreground font-normal"> · {pending} signup{pending > 1 ? "s" : ""} saved on this device</span>}
        </span>
      </div>
      {online && pending > 0 && (
        <Button data-testid="offline-sync" size="sm" onClick={doSync} disabled={syncing} className="rounded-full bg-primary font-bold gap-1.5">
          {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sync now
        </Button>
      )}
    </div>
  );
}

/* ---------- Quick volunteer ---------- */
function QuickVolunteer({ lgas }) {
  const { online } = useOffline();
  const [f, setF] = useState({ name: "", phone: "", lga: "", ward: "", pvc_status: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [savedOffline, setSavedOffline] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const lga = lgas.find((l) => l.name === f.lga);
  const valid = f.name && f.phone.length >= 11 && f.lga && f.ward && f.pvc_status;

  const submit = async () => {
    if (!navigator.onLine) {
      enqueueVolunteer(f);
      setSavedOffline(true);
      toast.success("Saved on this device. It will sync automatically when you're back online.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/volunteers", f);
      setResult(data);
      toast.success("You're connected to your coordinator!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (savedOffline) {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 text-center">
        <CloudUpload className="w-14 h-14 text-amber-500 mx-auto mb-4" />
        <h3 className="font-heading font-extrabold text-2xl">Saved Offline</h3>
        <p className="text-muted-foreground mt-3 max-w-md mx-auto">{f.name}'s details are stored safely on this device and will sync to the coordinator automatically once you have network again.</p>
        <Button className="mt-6 rounded-full bg-primary font-bold" data-testid="offline-new" onClick={() => { setSavedOffline(false); setF({ name: "", phone: "", lga: "", ward: "", pvc_status: "" }); }}>Add Another Volunteer</Button>
      </motion.div>
    );
  }

  if (result) {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 text-center">
        <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-4" />
        <h3 className="font-heading font-extrabold text-2xl">Welcome Aboard!</h3>
        <p className="text-muted-foreground mt-3 max-w-md mx-auto">{result.welcome_message}</p>
        {result.whatsapp && <a href={result.whatsapp} target="_blank" rel="noopener noreferrer"><Button className="mt-6 rounded-full bg-[#25D366] hover:bg-[#1eae57] font-bold gap-2" data-testid="vol-join-pod"><MessageCircle className="w-4 h-4" /> Join WhatsApp Pod</Button></a>}
      </motion.div>
    );
  }

  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8 space-y-4">
      <h3 className="font-heading font-bold text-xl">Quick Volunteer Signup</h3>
      <Field label="Full Name"><Input data-testid="vol-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" /></Field>
      <Field label="Phone Number"><Input data-testid="vol-phone" value={f.phone} onChange={(e) => set("phone", e.target.value.replace(/\D/g, ""))} placeholder="08030000000" maxLength={11} /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="LGA">
          <Select value={f.lga} onValueChange={(v) => { set("lga", v); set("ward", ""); }}>
            <SelectTrigger data-testid="vol-lga"><SelectValue placeholder="Select LGA" /></SelectTrigger>
            <SelectContent>{lgas.map((l) => <SelectItem key={l.code} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Ward">
          <Select value={f.ward} onValueChange={(v) => set("ward", v)} disabled={!lga}>
            <SelectTrigger data-testid="vol-ward"><SelectValue placeholder="Select Ward" /></SelectTrigger>
            <SelectContent>{lga?.wards.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="PVC Status">
        <Select value={f.pvc_status} onValueChange={(v) => set("pvc_status", v)}>
          <SelectTrigger data-testid="vol-pvc"><SelectValue placeholder="Select PVC status" /></SelectTrigger>
          <SelectContent>{PVC.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Button data-testid="vol-submit" onClick={submit} disabled={!valid || loading} className="w-full rounded-full bg-primary font-bold gap-2 py-6">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (!online ? <CloudUpload className="w-4 h-4" /> : null)} {online ? "Sign Up & Connect" : "Save Offline"}</Button>
    </div>
  );
}

/* ---------- PVC Helpdesk ---------- */
function PvcDesk({ faqs, centers }) {
  const [lang, setLang] = useState("en");
  return (
    <div className="space-y-8">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-bold text-xl flex items-center gap-2"><HelpCircle className="w-5 h-5 text-primary" /> INEC Helpdesk FAQ</h3>
          <div className="flex bg-muted rounded-full p-1 text-sm font-semibold">
            <button data-testid="faq-lang-en" onClick={() => setLang("en")} className={`px-3 py-1 rounded-full ${lang === "en" ? "bg-primary text-white" : "text-slate-600"}`}>English</button>
            <button data-testid="faq-lang-ha" onClick={() => setLang("ha")} className={`px-3 py-1 rounded-full ${lang === "ha" ? "bg-primary text-white" : "text-slate-600"}`}>Hausa</button>
          </div>
        </div>
        <Accordion type="single" collapsible className="space-y-2">
          {faqs.map((q, i) => (
            <AccordionItem key={i} value={`f${i}`} data-testid={`faq-${i}`} className="border border-slate-100 rounded-2xl px-4">
              <AccordionTrigger className="hover:no-underline text-left font-semibold text-sm">{lang === "en" ? q.q_en : q.q_ha}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{lang === "en" ? q.a_en : q.a_ha}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8">
        <h3 className="font-heading font-bold text-xl flex items-center gap-2 mb-5"><MapPin className="w-5 h-5 text-primary" /> INEC Collection Centers</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {centers.map((c, i) => (
            <div key={i} data-testid={`center-${i}`} className="border border-slate-100 rounded-2xl p-4 bg-muted/30">
              <div className="text-xs uppercase text-primary font-bold">{c.lga}</div>
              <div className="font-semibold mt-1">{c.name}</div>
              <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {c.address}</div>
              <div className="text-sm text-muted-foreground flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {c.phone}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
